import React, { useRef, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import FeedbackCollector, { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import Link from '@atlaskit/link';
import { type SurfaceMetadata } from '@atlassian/ai-smart-button-api';
import { type ProfileData } from '@atlassian/conversation-assistant-store';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { DynamicSurfaceActionItem } from '../action-item';

const FEEDBACK_ENTRYPOINT_ID = '9a6e0f65-eca3-4f73-8e40-5d4c466fee65';
const FEEDBACK_CONTEXT_CF = 'customfield_10047';
const LINK_TO_PRIVACY_POLICY = 'https://www.atlassian.com/legal/privacy-policy';

export const i18n = defineMessages({
	giveFeedbackActionTitle: {
		id: 'ai-smart-button.feedback-action.title',
		defaultMessage: 'Give feedback',
		description: 'The title for the give feedback AI action',
	},
	feedbackIconLabel: {
		id: 'ai-smart-button.feedback-item.icon-label',
		defaultMessage: 'Feedback icon',
		description: 'Label for the feedback icon',
	},
	feedbackTitle: {
		id: 'quick-summary.feedback.title',
		defaultMessage: 'Help us improve Atlassian Intelligence',
		description: 'Label text for the summary section of the report feedback collector',
	},
	feedbackSummaryPlaceholder: {
		id: 'quick-summary.feedback.summary.placeholder',
		defaultMessage: 'What did Atlassian Intelligence do well? What could be improved?',
		description: 'Description text for the summary section of the report feedback collector',
	},
	feedbackModalSelectFeedbackHeading: {
		id: 'ai-smart-button.feedback-action.select-feedback-heading',
		defaultMessage: 'Select feedback',
		description: 'Message displayed in subheading of select feedback dropdown',
	},
	chooseFeedbackOption: {
		id: 'ai-smart-button.feedback-action.choose-feedback-option',
		defaultMessage: 'Choose one',
		description: 'Message displayed in choose feedback option dropdown',
	},
	askQuestionOption: {
		id: 'ai-smart-button.feedback-action.ask-question-option',
		defaultMessage: 'Ask a question',
		description: 'Message displayed in ask question option of feedback dropdown',
	},
	leaveCommentOption: {
		id: 'ai-smart-button.feedback-action.leave-comment-option',
		defaultMessage: 'Leave a comment',
		description: 'Message displayed in leave a comment option of feedback dropdown',
	},
	reportBugOption: {
		id: 'ai-smart-button.feedback-action.report-bug-option',
		defaultMessage: 'Report a bug',
		description: 'Message displayed in report a bug option of feedback dropdown',
	},
	suggestImprovementOption: {
		id: 'ai-smart-button.feedback-action.suggest-improvement-option',
		defaultMessage: 'Suggest an improvement',
		description: 'New message displayed to suggest improvement',
	},
	feedbackQuestionHeading: {
		id: 'ai-smart-button.feedback-action.feedback-entry-question-heading',
		defaultMessage: 'What would you like to know?',
		description: 'Message displayed in feedback box heading when ask question option is selected',
	},
	feedbackCommentHeading: {
		id: 'ai-smart-button.feedback-action.feedback-entry-comment-heading',
		defaultMessage: "Let us know what's on your mind",
		description: 'Message displayed in feedback box heading when comment option is selected',
	},
	feedbackBugHeading: {
		id: 'ai-smart-button.feedback-action.feedback-entry-bug-heading',
		defaultMessage: 'Describe the bug or issue',
		description: 'Message displayed in feedback box heading when report bug option is selected',
	},
	feedbackSuggestHeading: {
		id: 'ai-smart-button.feedback-action.feedback-entry-suggest-heading',
		defaultMessage: "Let us know what you'd like to improve",
		description:
			'Message displayed in feedback box heading when suggest improvement option is selected',
	},
	canContactMessage: {
		id: 'ai-smart-button.feedback-action.can-contact-message',
		defaultMessage:
			'Yes, {product} teams can contact me to learn about my experiences to improve Atlassian products and services. I acknowledge the {privacyPolicy}',
		description: 'Message displayed in contact agreement checkbox',
	},
	researchParticipationMessage: {
		id: 'ai-smart-button.feedback-action.research-participation-message',
		defaultMessage: "I'd like to participate in product research",
		description: 'Message displayed in research participation agreement checkbox',
	},
	privacyPolicyLink: {
		id: 'ai-smart-button.feedback-action.privacy-policy-link',
		defaultMessage: '{product} Privacy Policy.',
		description: 'Text of hyperlink to Atlassian privacy policy',
	},
	submitButtonLabel: {
		id: 'ai-smart-button.feedback-action.submit-button-label',
		defaultMessage: 'Send feedback',
		description: 'Label for submit button',
	},
	cancelButtonLabel: {
		id: 'ai-smart-button.feedback-action.cancel-button-label',
		defaultMessage: 'Cancel',
		description: 'Label for cancel button',
	},
});

const PrivacyPolicyLink = () => (
	<Link target="_blank" href={LINK_TO_PRIVACY_POLICY}>
		<FormattedMessage {...i18n.privacyPolicyLink} values={{ product: 'Atlassian' }} />
	</Link>
);

export const FeedbackActionItem = ({
	surfaceMetadata,
	profileData,
}: {
	profileData: ProfileData;
	surfaceMetadata: SurfaceMetadata;
}) => {
	const ref = useRef<HTMLElement | null>(null);
	const intl = useIntl();
	const { locale: intlLocale } = intl;

	const [isOpen, setIsOpen] = useState(false);
	const [displayFlag, setDisplayFlag] = useState(false);

	const openFeedbackDialog = () => setIsOpen(true);
	const closeFeedbackDialog = () => setIsOpen(false);
	const displayFlagTrue = () => setDisplayFlag(true);
	const hideFlag = () => setDisplayFlag(false);

	const feedbackContext = `product: ${surfaceMetadata.product} \n pageMode: ${surfaceMetadata.pageMode}`;

	return (
		<>
			<DynamicSurfaceActionItem
				title={intl.formatMessage(i18n.giveFeedbackActionTitle)}
				icon={<FeedbackIcon label={intl.formatMessage(i18n.feedbackIconLabel)} />}
				status={undefined}
				onClick={openFeedbackDialog}
			/>

			{isOpen && (
				<FeedbackCollector
					onClose={closeFeedbackDialog}
					entrypointId={FEEDBACK_ENTRYPOINT_ID}
					shouldReturnFocusRef={ref}
					onSubmit={displayFlagTrue}
					atlassianAccountId={profileData.aaid ? profileData.aaid : undefined}
					name={profileData.name}
					additionalFields={[
						{
							id: FEEDBACK_CONTEXT_CF,
							value: feedbackContext,
						},
					]}
					feedbackTitle={intl.formatMessage(i18n.feedbackTitle)}
					summaryPlaceholder={intl.formatMessage(i18n.feedbackSummaryPlaceholder)}
					customFeedbackOptions={[
						{
							label: intl.formatMessage(i18n.askQuestionOption),
							value: 'question',
						},
						{
							label: intl.formatMessage(i18n.leaveCommentOption),
							value: 'comment',
						},
						{
							label: intl.formatMessage(i18n.reportBugOption),
							value: 'bug',
						},
						{
							label: intl.formatMessage(i18n.suggestImprovementOption),
							value: 'suggestion',
						},
					]}
					selectLabel={intl.formatMessage(i18n.feedbackModalSelectFeedbackHeading)}
					feedbackGroupLabels={{
						empty: {
							fieldLabel: intl.formatMessage(i18n.chooseFeedbackOption),
							selectOptionLabel: intl.formatMessage(i18n.chooseFeedbackOption),
						},
						question: {
							fieldLabel: intl.formatMessage(i18n.feedbackQuestionHeading),
							selectOptionLabel: intl.formatMessage(i18n.feedbackQuestionHeading),
						},
						comment: {
							fieldLabel: intl.formatMessage(i18n.feedbackCommentHeading),
							selectOptionLabel: intl.formatMessage(i18n.feedbackCommentHeading),
						},
						bug: {
							fieldLabel: intl.formatMessage(i18n.feedbackBugHeading),
							selectOptionLabel: intl.formatMessage(i18n.feedbackBugHeading),
						},
						suggestion: {
							fieldLabel: intl.formatMessage(i18n.feedbackSuggestHeading),
							selectOptionLabel: intl.formatMessage(i18n.feedbackSuggestHeading),
						},
					}}
					enrolInResearchLabel={intl.formatMessage(i18n.researchParticipationMessage)}
					canBeContactedLabel={
						<FormattedMessage
							{...i18n.canContactMessage}
							values={{
								product: 'Atlassian',
								privacyPolicy: <PrivacyPolicyLink />,
							}}
						/>
					}
					cancelButtonLabel={intl.formatMessage(i18n.cancelButtonLabel)}
					submitButtonLabel={intl.formatMessage(i18n.submitButtonLabel)}
				/>
			)}

			<FlagGroup onDismissed={hideFlag}>
				{displayFlag && <FeedbackFlag locale={intlLocale} />}
			</FlagGroup>
		</>
	);
};
